import React, { useMemo, useState } from 'react';
import { Modal, ModalBody, ModalHeader, Button } from 'flowbite-react';
import type * as sched from 'sched';

interface ResolvedAssignment {
  primary: string;
  paramKey?: string;
  missing?: boolean;
}

interface WorkflowStepStatusModalProps {
  show: boolean;
  onClose: () => void;
  step: sched.WorkflowTemplateStep;
  stepState: any | null;
  onUpdateStepState: (stepId: string, state: any) => void;
  resolvedAssignment?: ResolvedAssignment;
  currentPersonId?: string;
  readOnly?: boolean;
  // For work log
  instanceId?: string;
  database?: sched.WrappedDatabase | null;
  people?: Array<{ id: string; name: string }>;
  applyCommand?: (command: sched.DatabaseCommand) => Promise<void>;
}

export const WorkflowStepStatusModal: React.FC<WorkflowStepStatusModalProps> = ({
  show,
  onClose,
  step,
  stepState,
  onUpdateStepState,
  resolvedAssignment,
  currentPersonId,
  readOnly = false,
  instanceId,
  database,
  people,
  applyCommand,
}) => {
  // Build the entity key for this workflow step
  const entityKey = instanceId ? `step:${instanceId}:${step.id}` : null;

  // Read existing manual allocations from the database
  const allocations = useMemo<any[]>(() => {
    if (!database || !entityKey) return [];
    const raw = database.manual_allocations_for(entityKey);
    if (!raw || !Array.isArray(raw)) return [];
    return raw;
  }, [database, entityKey]);

  return (
    <Modal show={show} onClose={onClose} size="lg">
      <ModalHeader>{step.title}</ModalHeader>
      <ModalBody>
        {/* Read-only config section */}
        <div className="space-y-3 mb-6">
          {step.description && (
            <div>
              <span className="text-xs font-medium text-cm-text-muted uppercase tracking-wider">Description</span>
              <p className="mt-1 text-sm text-cm-text-primary">{step.description}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-xs font-medium text-cm-text-muted uppercase tracking-wider">Type</span>
              <p className="mt-1 text-cm-text-primary">{step.completion.type}</p>
            </div>
            {step.completion.type === 'Manual' && step.completion.work_estimate && (
              <div>
                <span className="text-xs font-medium text-cm-text-muted uppercase tracking-wider">Estimate</span>
                <p className="mt-1 text-cm-text-primary">
                  {formatEstimate(step.completion.work_estimate)}
                </p>
              </div>
            )}
            {resolvedAssignment && resolvedAssignment.primary !== '—' && (
              <div>
                <span className="text-xs font-medium text-cm-text-muted uppercase tracking-wider">Assigned to</span>
                <p className={`mt-1 ${resolvedAssignment.missing ? 'text-amber-500' : 'text-cm-text-primary'}`}>
                  {resolvedAssignment.primary}
                </p>
                {resolvedAssignment.paramKey && (
                  <p className="text-xs text-cm-text-muted">
                    via parameter: {resolvedAssignment.paramKey}
                  </p>
                )}
              </div>
            )}
          </div>
          {step.condition && (
            <div>
              <span className="text-xs font-medium text-cm-text-muted uppercase tracking-wider">Condition</span>
              <p className="mt-1 text-sm text-cm-text-primary">
                {step.condition.type === 'BoolParameter'
                  ? `${(step.condition as any).negate ? 'Excluded' : 'Included'} when "${(step.condition as any).key}" is true`
                  : step.condition.type}
              </p>
            </div>
          )}
        </div>

        <hr className="border-cm-border-subtle mb-4" />

        {/* Status section */}
        <StepStatusSection
          step={step}
          stepState={stepState}
          onUpdateStepState={onUpdateStepState}
          currentPersonId={currentPersonId}
          readOnly={readOnly}
        />

        {/* Work log section — only for Manual steps */}
        {step.completion.type === 'Manual' && entityKey && applyCommand && (
          <>
            <hr className="border-cm-border-subtle my-4" />
            <WorkLogSection
              allocations={allocations}
              people={people ?? []}
              entityKey={entityKey}
              applyCommand={applyCommand}
              readOnly={readOnly}
            />
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

// ─── Work Log ─────────────────────────────────────────────────────────────

const WorkLogSection: React.FC<{
  allocations: any[];
  people: Array<{ id: string; name: string }>;
  entityKey: string;
  applyCommand: (command: sched.DatabaseCommand) => Promise<void>;
  readOnly: boolean;
}> = ({ allocations, people, entityKey, applyCommand, readOnly }) => {
  const [personId, setPersonId] = useState('');
  const [units, setUnits] = useState('1');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  const personName = (id: string) => people.find(p => p.id === id)?.name ?? id.slice(0, 8);

  const totalUnits = allocations.reduce((sum, a) => sum + (a.time ?? 0), 0);

  const handleAdd = async () => {
    if (!personId || !units || !date) return;
    setSubmitting(true);
    try {
      const command: sched.DatabaseCommand = {
        type: 'IncrementResourceAllocation',
        id: crypto.randomUUID(),
        increment: parseInt(units, 10),
        entity: entityKey,
        person_id: personId,
        when: { type: 'Day', day: date },
      };
      await applyCommand(command);
      setUnits('1');
    } catch (err) {
      console.error('Failed to add allocation:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <span className="text-xs font-medium text-cm-text-muted uppercase tracking-wider">
        Work Log
        {totalUnits > 0 && <span className="ml-1 normal-case">({totalUnits} units)</span>}
      </span>

      {allocations.length > 0 ? (
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="text-xs text-cm-text-muted">
              <th className="text-left font-medium py-1">Who</th>
              <th className="text-left font-medium py-1">When</th>
              <th className="text-right font-medium py-1">Units</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {allocations.map((a, i) => (
              <tr key={a.id ?? i} className="text-cm-text-primary">
                <td className="py-1">{personName(a.personId ?? a.person_id)}</td>
                <td className="py-1 text-xs">{formatEpoch(a.when)}</td>
                <td className="py-1 text-right">{a.time}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="mt-2 text-sm text-cm-text-muted">No work recorded yet.</p>
      )}

      {!readOnly && (
        <div className="mt-3 grid grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
          <div>
            <label className="block text-xs text-cm-text-muted mb-0.5">Person</label>
            <select
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
              className="w-full rounded border border-cm-border-subtle bg-cm-bg-surface px-2 py-1.5 text-sm"
            >
              <option value="">Select</option>
              {people.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-cm-text-muted mb-0.5">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded border border-cm-border-subtle bg-cm-bg-surface px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-cm-text-muted mb-0.5">Units</label>
            <input
              type="number"
              min="1"
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              className="w-16 rounded border border-cm-border-subtle bg-cm-bg-surface px-2 py-1.5 text-sm text-center"
            />
          </div>
          <div>
            <Button
              size="sm"
              color="blue"
              disabled={!personId || !units || submitting}
              onClick={handleAdd}
            >
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

function formatEpoch(when: any): string {
  if (!when) return '—';
  if (when.type === 'Day') return when.day;
  if (when.type === 'Days') return `${when.start_inclusive}–${when.end_inclusive}`;
  return JSON.stringify(when);
}

// ─── Step status ──────────────────────────────────────────────────────────

const StepStatusSection: React.FC<{
  step: sched.WorkflowTemplateStep;
  stepState: any | null;
  onUpdateStepState: (stepId: string, state: any) => void;
  currentPersonId?: string;
  readOnly: boolean;
}> = ({ step, stepState, onUpdateStepState, currentPersonId, readOnly }) => {
  const completion = step.completion;
  const isCompleted = stepState?.completed_datetime != null;

  if (completion.type === 'Manual') {
    return (
      <ManualStepStatus
        stepId={step.id}
        isCompleted={isCompleted}
        completedAt={stepState?.completed_datetime}
        onMarkComplete={(datetime) => {
          onUpdateStepState(step.id, { completed_datetime: datetime });
        }}
        readOnly={readOnly}
      />
    );
  }

  if (completion.type === 'Branch') {
    const options: Record<string, string> = (completion as any).options || {};
    const decidedBranches: string[] = stepState?.decision?.options_selected || [];

    return (
      <BranchDecisionStatus
        stepId={step.id}
        options={options}
        decidedBranches={decidedBranches}
        onRecordDecision={(branches) => {
          onUpdateStepState(step.id, {
            decision: {
              options_selected: branches,
              decider: currentPersonId ?? '00000000-0000-0000-0000-000000000000',
              decided_at: new Date().toISOString(),
              note: null,
            },
          });
        }}
        readOnly={readOnly}
      />
    );
  }

  return (
    <div className="text-sm text-cm-text-muted">
      <span className="font-medium">Status: </span>
      {isCompleted ? (
        <span className="text-green-600">Completed</span>
      ) : (
        <span className="text-cm-text-muted">Pending (auto-resolves)</span>
      )}
    </div>
  );
};

const ManualStepStatus: React.FC<{
  stepId: string;
  isCompleted: boolean;
  completedAt: string | null;
  onMarkComplete: (datetime: string) => void;
  readOnly: boolean;
}> = ({ stepId, isCompleted, completedAt, onMarkComplete, readOnly }) => {
  const [datetime, setDatetime] = useState(() => {
    return new Date().toISOString().slice(0, 16);
  });

  if (isCompleted) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="inline-flex items-center gap-1 text-green-600">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Completed
        </span>
        {completedAt && (
          <span className="text-cm-text-muted text-xs">
            {new Date(completedAt).toLocaleString()}
          </span>
        )}
      </div>
    );
  }

  if (readOnly) {
    return <div className="text-sm text-cm-text-muted">Pending</div>;
  }

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-cm-text-muted uppercase tracking-wider">
        Completion date
      </label>
      <div className="flex items-center gap-3">
        <input
          type="datetime-local"
          value={datetime}
          onChange={(e) => setDatetime(e.target.value)}
          className="rounded border border-cm-border-subtle bg-cm-bg-surface px-2 py-1.5 text-sm"
        />
        <Button
          size="sm"
          color="green"
          onClick={() => onMarkComplete(new Date(datetime).toISOString())}
        >
          Mark Complete
        </Button>
      </div>
    </div>
  );
};

const BranchDecisionStatus: React.FC<{
  stepId: string;
  options: Record<string, string>;
  decidedBranches: string[];
  onRecordDecision: (branches: string[]) => void;
  readOnly: boolean;
}> = ({ stepId, options, decidedBranches, onRecordDecision, readOnly }) => {
  const [selected, setSelected] = useState<string[]>(decidedBranches);

  if (decidedBranches.length > 0) {
    return (
      <div className="text-sm">
        <span className="font-medium text-cm-text-muted">Decision: </span>
        <span className="text-green-600">
          {decidedBranches.map(b => options[b] || b).join(', ')}
        </span>
      </div>
    );
  }

  if (readOnly) {
    return <div className="text-sm text-cm-text-muted">Pending decision</div>;
  }

  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-cm-text-muted">Choose branch:</span>
      <div className="space-y-1">
        {Object.entries(options).map(([optId, optLabel]) => (
          <label key={optId} className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name={`decision-${stepId}`}
              checked={selected.includes(optId)}
              onChange={() => setSelected([optId])}
              className="border-cm-border-subtle"
            />
            {optLabel}
          </label>
        ))}
      </div>
      <Button
        size="sm"
        color="blue"
        disabled={selected.length === 0}
        onClick={() => onRecordDecision(selected)}
      >
        Record Decision
      </Button>
    </div>
  );
};

function formatEstimate(est: any): string {
  const parts: string[] = [];
  if (est.min != null) parts.push(`min: ${est.min}`);
  if (est.expected != null) parts.push(`exp: ${est.expected}`);
  if (est.max != null) parts.push(`max: ${est.max}`);
  return parts.join(', ') || 'None';
}

export default WorkflowStepStatusModal;
